import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Tag, Loader2, Edit2, Save, X, Plus, ExternalLink, AlertCircle } from "lucide-react";
import { api, formatErr } from "../../lib/api";

/**
 * Admin → Affiliate Partners
 *
 * Manage AWIN affiliate brands without code edits. CRUD over /api/admin/
 * affiliate/brands. The AWIN Publisher ID (used for ALL brands) is read from
 * the AWIN_PUBLISHER_ID env var — surfaced read-only in this card so admin
 * knows whether the redirect wrapper will actually fire or fall through to
 * a direct link.
 */
export default function AffiliatePartnersCard() {
  const [brands, setBrands] = useState([]);
  const [publisherSet, setPublisherSet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // slug | "_new"
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/affiliate/brands");
      setBrands(data.brands || []);
      setPublisherSet(!!data.publisher_id_set);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (b) => {
    setEditing(b.slug);
    setForm({
      ...b,
      keywords: (b.keywords || []).join(", "),
    });
  };

  const startNew = () => {
    setEditing("_new");
    setForm({ slug: "", name: "", root_url: "", awin_mid: "", keywords: "", category: "", blurb: "", enabled: true });
  };

  const cancel = () => { setEditing(null); setForm({}); };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        keywords: String(form.keywords || "")
          .split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (editing === "_new") {
        await api.post("/admin/affiliate/brands", payload);
        toast.success(`Brand "${form.name}" added`);
      } else {
        const { slug, ...rest } = payload;
        await api.patch(`/admin/affiliate/brands/${editing}`, rest);
        toast.success("Saved");
      }
      cancel();
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const remove = async (slug, name) => {
    if (!window.confirm(`Delete "${name}"? Existing clicks remain in analytics but new links will stop working.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/affiliate/brands/${slug}`);
      toast.success(`"${name}" deleted`);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-10 p-5 border hairline bg-white" data-testid="affiliate-partners-card">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5">
            <Tag className="w-3 h-3" /> Affiliate partners
          </div>
          <div className="font-display text-xl mt-1">AWIN merchants</div>
          <p className="text-sm text-[#555] max-w-2xl mt-1">
            Each brand below maps to one AWIN merchant. Mentions of the listed
            keywords in community threads + AI chat replies + the campsite sidebar
            auto-link through <code className="mono text-[12px]">/api/r/&lt;slug&gt;</code>,
            which records the click and wraps the destination in the AWIN deep-link.
          </p>
        </div>
        <button onClick={startNew} className="btn-primary !px-3 !py-2 text-xs inline-flex items-center gap-1.5" data-testid="new-brand-btn">
          <Plus className="w-3.5 h-3.5" /> New brand
        </button>
      </div>

      {/* AWIN env var status */}
      <div className={`border hairline p-3 mb-4 text-[12px] flex items-start gap-2 ${publisherSet ? "bg-[#f4fff0]" : "bg-[#fff4f0]"}`} data-testid="awin-publisher-status">
        {publisherSet ? (
          <>
            <span className="text-[#0a8500]">✓</span>
            <div><strong>AWIN Publisher ID is configured.</strong> Every brand with a merchant ID set below will route through the AWIN deep-link wrapper so commission is tracked.</div>
          </>
        ) : (
          <>
            <AlertCircle className="w-4 h-4 text-[#b00020] flex-shrink-0 mt-0.5" />
            <div>
              <strong>AWIN_PUBLISHER_ID is not set.</strong> Add it to your backend <code className="mono">.env</code> file as <code className="mono">AWIN_PUBLISHER_ID=&lt;your 6-digit ID&gt;</code> then redeploy. Until then, partner links work but commission won't be tracked.
            </div>
          </>
        )}
      </div>

      {/* New-brand inline form */}
      {editing === "_new" && (
        <EditRow form={form} setForm={setForm} onSave={save} onCancel={cancel} busy={busy} isNew />
      )}

      {/* List */}
      {loading ? (
        <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : brands.length === 0 ? (
        <div className="p-8 border hairline bg-[#fafafa] text-center text-sm text-[#888]">No brands yet — tap "New brand" to add one.</div>
      ) : (
        <div className="border hairline divide-y" data-testid="brand-list">
          {brands.map((b) => (
            editing === b.slug ? (
              <div key={b.slug} className="p-4 bg-[#fafafa]">
                <EditRow form={form} setForm={setForm} onSave={save} onCancel={cancel} busy={busy} />
              </div>
            ) : (
              <div key={b.slug} className="p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 hover:bg-[#fafafa]" data-testid={`brand-${b.slug}`}>
                <div className="flex-1 min-w-0 w-full">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-base break-words">{b.name}</span>
                    <span className="mono text-[10px] text-[#888] uppercase">/{b.slug}</span>
                    {b.awin_mid
                      ? <span className="mono text-[10px] uppercase bg-[#f4fff0] border hairline px-1.5 py-0.5 text-[#0a8500]">mid {b.awin_mid}</span>
                      : <span className="mono text-[10px] uppercase bg-[#fff4f0] border hairline px-1.5 py-0.5 text-[#b00020]">no mid · direct link only</span>}
                    {!b.enabled && <span className="mono text-[10px] uppercase text-[#888]">disabled</span>}
                  </div>
                  <div className="text-[12px] text-[#555] mt-1 min-w-0">
                    {b.category && <span>{b.category} · </span>}
                    <a href={b.root_url} target="_blank" rel="noopener noreferrer" className="text-[#ff4500] underline break-all inline-flex items-baseline gap-1">
                      <span className="break-all">{b.root_url}</span>
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                  </div>
                  {(b.keywords || []).length > 0 && (
                    <div className="text-[11px] text-[#888] mt-1.5 mono flex flex-wrap items-center gap-1">
                      <span>Triggers:</span>
                      {(b.keywords || []).slice(0, 6).map((k) => <span key={k} className="bg-[#fafafa] border hairline px-1.5 py-0.5 break-all">{k}</span>)}
                      {b.keywords.length > 6 && <span className="text-[#aaa]">+{b.keywords.length - 6} more</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 flex-wrap sm:flex-nowrap sm:justify-end w-full sm:w-auto pt-1 sm:pt-0 border-t sm:border-t-0 hairline sm:border-0">
                  <a href={`/api/r/${b.slug}`} target="_blank" rel="noopener noreferrer" className="btn-secondary !px-2 !py-1 text-[10px] inline-flex items-center gap-1" title="Test the redirect" data-testid={`test-${b.slug}`}>
                    <ExternalLink className="w-3 h-3" /> Test
                  </a>
                  <button onClick={() => startEdit(b)} className="btn-secondary !px-2 !py-1 text-[10px] inline-flex items-center gap-1" data-testid={`edit-${b.slug}`}>
                    <Edit2 className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => remove(b.slug, b.name)} className="btn-secondary !px-2 !py-1 text-[10px] inline-flex items-center gap-1 text-[#b00020]" data-testid={`del-${b.slug}`}>
                    <X className="w-3 h-3" /> Del
                  </button>
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}

function EditRow({ form, setForm, onSave, onCancel, busy, isNew = false }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="space-y-2.5">
      <div className="grid sm:grid-cols-2 gap-2.5">
        <div>
          <div className="eyebrow text-[10px] mb-1">Slug (URL-safe)</div>
          <input value={form.slug || ""} disabled={!isNew} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
            className="w-full border hairline p-2 text-sm mono disabled:bg-[#eee]"
            placeholder="e.g. teva-uk" data-testid="brand-slug" />
        </div>
        <div>
          <div className="eyebrow text-[10px] mb-1">Display name</div>
          <input value={form.name || ""} onChange={(e) => set("name", e.target.value)}
            className="w-full border hairline p-2 text-sm" placeholder="Teva UK" data-testid="brand-name" />
        </div>
      </div>
      <div className="grid sm:grid-cols-[1fr_140px_140px] gap-2.5">
        <div>
          <div className="eyebrow text-[10px] mb-1">Root URL</div>
          <input value={form.root_url || ""} onChange={(e) => set("root_url", e.target.value)}
            className="w-full border hairline p-2 text-sm mono" placeholder="https://www.teva.com" data-testid="brand-url" />
        </div>
        <div>
          <div className="eyebrow text-[10px] mb-1">AWIN merchant ID</div>
          <input value={form.awin_mid || ""} onChange={(e) => set("awin_mid", e.target.value)}
            className="w-full border hairline p-2 text-sm mono" placeholder="e.g. 12345" data-testid="brand-mid" />
        </div>
        <div>
          <div className="eyebrow text-[10px] mb-1">Category</div>
          <input value={form.category || ""} onChange={(e) => set("category", e.target.value)}
            className="w-full border hairline p-2 text-sm" placeholder="Footwear" data-testid="brand-category" />
        </div>
      </div>
      <div>
        <div className="eyebrow text-[10px] mb-1">Keywords (comma-separated) — what to auto-link in threads &amp; AI chat</div>
        <input value={form.keywords || ""} onChange={(e) => set("keywords", e.target.value)}
          className="w-full border hairline p-2 text-sm" placeholder="Teva, walking sandals, Hurricane XLT" data-testid="brand-keywords" />
      </div>
      <div>
        <div className="eyebrow text-[10px] mb-1">Blurb (shown on campsite sidebar)</div>
        <textarea value={form.blurb || ""} onChange={(e) => set("blurb", e.target.value)}
          className="w-full border hairline p-2 text-sm" rows={2}
          placeholder="One-sentence pitch — what they sell, why a camper might want it." data-testid="brand-blurb" />
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={form.enabled !== false} onChange={(e) => set("enabled", e.target.checked)} className="accent-[#ff4500]" />
          Enabled (auto-link mentions, show in sidebars)
        </label>
        <div className="flex items-center gap-1.5">
          <button onClick={onCancel} disabled={busy} className="btn-secondary !px-3 !py-2 text-xs">Cancel</button>
          <button onClick={onSave} disabled={busy || !form.name || !form.slug || !form.root_url}
            className="btn-primary !px-3 !py-2 text-xs inline-flex items-center gap-1.5"
            data-testid="save-brand-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
